import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button-variants";
import { Link, useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";
import OrganizationSchema from "@/components/seo/OrganizationSchema";
import SearchBar from "@/components/home/SearchBar";
import KeyStats from "@/components/home/KeyStats";
import SpontaneousApplication from "@/components/home/SpontaneousApplication";
import FoundersStory from "@/components/home/FoundersStory";
import RecruitmentProcess from "@/components/home/RecruitmentProcess";
import SpecialtiesGrid from "@/components/home/SpecialtiesGrid";
import BlogSection from "@/components/home/BlogSection";
import doctorsTeamImage from "@/assets/doctors-team.jpg";
import heroDoctor from "@/assets/hero-medecin.webp";

const Home = () => {
  const navigate = useNavigate();

  const handlePopularSearch = (specialty: string, location: string) => {
    const params = new URLSearchParams();
    params.set("specialty", specialty);
    params.set("location", location);
    navigate(`/offres?${params.toString()}`);
  };

  return (
    <>
      <OrganizationSchema />
      <div>
      <SEO 
        title="Agence d'intérim médical en Île-de-France"
        description="Trouvez votre prochaine mission médicale en Île-de-France : missions sur-mesure, accompagnement 100% humain, zéro paperasse. +500 médecins nous font confiance."
        keywords="intérim médical, agence interim medical, missions médicales, médecin remplaçant, emploi médecin, Île-de-France"
        canonical="/"
        ogTitle="Votre prochaine mission médicale commence ici"
        ogDescription="Missions sur-mesure en Île-de-France, un interlocuteur unique, zéro paperasse. +500 médecins nous font confiance."
        twitterDescription="Missions médicales sur-mesure en Île-de-France. Accompagnement 100% humain, zéro paperasse. Inscription gratuite en 2 min."
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary/95 to-secondary overflow-hidden">
        {/* Enhanced animated background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-secondary/30 via-transparent to-transparent"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent"></div>
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
        </div>
        
        {/* Dynamic floating shapes */}
        <div className="absolute top-20 -right-20 w-96 h-96 bg-gradient-to-br from-secondary/20 to-transparent rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 -left-20 w-[500px] h-[500px] bg-gradient-to-tr from-white/10 to-transparent rounded-full blur-3xl animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-secondary/5 rounded-full blur-2xl animate-pulse delay-300"></div>

        {/* Bottom fade to next section - composite of KeyStats bg (white + primary/5) */}
        <div className="absolute bottom-0 inset-x-0 h-16 md:h-20 bg-gradient-to-b from-transparent via-background/40 to-background/90 pointer-events-none z-20"></div>
        <div className="absolute bottom-0 inset-x-0 h-16 md:h-20 bg-gradient-to-b from-transparent to-primary/5 pointer-events-none z-20"></div>


        <div className="container mx-auto px-8 md:px-12 lg:px-16 pt-4 md:pt-6 lg:pt-0 pb-8 md:pb-16 lg:pb-0 relative z-30">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_420px] gap-8 lg:gap-10 items-end max-w-7xl mx-auto">

            <div className="relative animate-fade-in delay-300 mt-6 md:mt-12 lg:mt-0 mb-8 md:mb-12 lg:mb-20 w-full max-w-xl md:max-w-2xl mx-auto lg:mx-0 lg:ml-24 xl:ml-40 2xl:ml-56 lg:max-w-[560px] xl:max-w-[600px]">

              {/* Premium badge - above the card */}
              <div className="flex justify-center mb-4 md:mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-5 md:py-2.5 bg-white/10 backdrop-blur-md rounded-full border border-white/30 shadow-lg hover:bg-white/15 transition-all duration-300 cursor-default">
                  <span className="text-white/95 text-xs md:text-sm font-semibold tracking-wide">Agence Interim Médical Ile-de-France</span>
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-400 rounded-full animate-pulse"></div>
                </div>
              </div>
              <div className="relative group animate-[float_6s_ease-in-out_infinite]">
                {/* Glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-white/20 to-secondary/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
                
                {/* Speech bubble tail */}
                <div className="absolute -bottom-4 left-12 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[20px] border-t-white z-10"></div>
                
                {/* Card */}
                <div className="relative bg-white rounded-3xl shadow-2xl border border-white/40 overflow-hidden hover:shadow-[0_25px_80px_-15px_rgba(0,0,0,0.4)] transition-all duration-700">
                  <div className="p-6 md:p-8">
                    {/* Welcome message */}
                    <div className="mb-5 text-center">
                      <h2 className="font-poppins font-black text-2xl md:text-3xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                        Bonjour Docteur 👋
                      </h2>
                      <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-medium">
                        Quelle mission vous fait rêver aujourd'hui ?
                      </p>
                    </div>
                    
                    {/* Search Bar */}
                    <SearchBar />

                    {/* Quick suggestions */}
                    <div className="border-t border-border/50 pt-4 mt-4">
                      <p className="text-xs font-semibold text-muted-foreground mb-3 flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Recherches populaires
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {[
                          { text: "Anesthésiste-Réanimateur", specialty: "Anesthésiste-Réanimateur", location: "" },
                          { text: "Urgentiste", specialty: "Urgentiste", location: "" },
                          { text: "Généraliste Saone-Et-Loire", specialty: "Généraliste", location: "SAONE-ET-LOIRE/71" }
                        ].map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => handlePopularSearch(suggestion.specialty, suggestion.location)}
                            className="px-4 py-2 bg-gradient-to-r from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 border border-primary/20 hover:border-primary/40 rounded-full text-xs font-medium text-foreground transition-all duration-300 hover:scale-105 hover:shadow-md"
                            aria-label={`Rechercher ${suggestion.text}`}
                          >
                            {suggestion.text}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Doctor - right column, visible from lg */}
            <div className="hidden lg:flex items-end justify-center self-end relative -mb-px">
              <img
                src={heroDoctor}
                alt="Médecin Mind Medical - intérim médical en France"
                width="580"
                height="780"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full max-w-[340px] xl:max-w-[420px] 2xl:max-w-[480px] h-auto object-contain object-bottom pointer-events-none select-none drop-shadow-2xl animate-fade-in"
              />

            </div>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <KeyStats />

      {/* Recruitment Process & Account Creation */}
      <RecruitmentProcess />

      {/* Specialties Grid */}
      <SpecialtiesGrid />

      {/* Founders Story & Why Choose Us */}
      <FoundersStory />

      {/* Spontaneous Application */}
      <SpontaneousApplication />

      {/* Blog Section */}
      <BlogSection />
    </div>
    </>
  );
};

// Helper component
const CheckCircle = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export default Home;